// Procedural CSS/SVG forest scene — ported from design_ideas/lernwelt/scene.jsx.
// When a baked scene PNG exists at /illustrations/scenes/{arc}__{chapter}.png,
// it fades in on top of the procedural scene; otherwise the procedural scene
// stays as the backdrop.

import { useEffect, useMemo, useState } from 'react';
import { sceneBackgroundUrl } from '../../assets/illustrations';

const PALETTES: Record<string, {
  sky1: string; sky2: string; moon: string;
  tree1: string; tree2: string; tree3: string;
  ground: string; highlight: string;
}> = {
  schatzsuche:  { sky1:'#1e3128', sky2:'#325044', moon:'#f2d9a0', tree1:'#0e1c18', tree2:'#172821', tree3:'#203328', ground:'#3a2e1e', highlight:'#d8a45a' },
  drachenhoehle:{ sky1:'#2a1a2a', sky2:'#432937', moon:'#e8b37a', tree1:'#180f1a', tree2:'#241727', tree3:'#2e2032', ground:'#3a2520', highlight:'#d07a5a' },
  zaubermarkt:  { sky1:'#1b2730', sky2:'#32414c', moon:'#f4dca8', tree1:'#101a1e', tree2:'#182429', tree3:'#223038', ground:'#2f2a22', highlight:'#e5b465' },
};

function Tree({ height = 100, color = '#14261f', wide = false }: { height?: number; color?: string; wide?: boolean }) {
  const w = wide ? height * 0.75 : height * 0.55;
  return (
    <div style={{ position:'relative', width:w, height, flexShrink:0, filter:'drop-shadow(0 4px 6px rgba(0,0,0,0.22))' }}>
      <div style={{ position:'absolute', left:'50%', transform:'translateX(-50%)', bottom:0, width:0, height:0, borderLeft:`${w/2}px solid transparent`, borderRight:`${w/2}px solid transparent`, borderBottom:`${height}px solid ${color}` }} />
      <div style={{ position:'absolute', left:'50%', transform:'translateX(-50%)', bottom:height*0.3, width:0, height:0, borderLeft:`${w*0.4}px solid transparent`, borderRight:`${w*0.4}px solid transparent`, borderBottom:`${height*0.7}px solid ${color}` }} />
      <div style={{ position:'absolute', left:'50%', transform:'translateX(-50%)', bottom:height*0.55, width:0, height:0, borderLeft:`${w*0.28}px solid transparent`, borderRight:`${w*0.28}px solid transparent`, borderBottom:`${height*0.5}px solid ${color}` }} />
    </div>
  );
}

function Stars() {
  const stars = useMemo(() => {
    const out: { x: number; y: number; sz: number; delay: number }[] = [];
    let s = 42;
    for (let i = 0; i < 36; i++) {
      s = (s * 9301 + 49297) % 233280;
      const x = (s / 233280) * 100;
      s = (s * 9301 + 49297) % 233280;
      const y = (s / 233280) * 55;
      s = (s * 9301 + 49297) % 233280;
      const sz = 1 + (s / 233280) * 2.2;
      out.push({ x, y, sz, delay: (i % 7) * 0.3 });
    }
    return out;
  }, []);
  return (
    <>
      {stars.map((st, i) => (
        <div key={i} style={{ position:'absolute', top:`${st.y}%`, left:`${st.x}%`, width:st.sz, height:st.sz, borderRadius:'50%', background:'#f7ecd0', opacity:0.88, boxShadow:`0 0 ${st.sz*2}px #f7ecd088`, animation:`star-twinkle 3.5s ease-in-out ${st.delay}s infinite` }} />
      ))}
    </>
  );
}

function Lantern({ top, left, color, delay = 0 }: { top: string; left: string; color: string; delay?: number }) {
  return (
    <div style={{ position:'absolute', top, left, width:12, height:16, animation:`lantern-float 6s ease-in-out ${delay}s infinite` }}>
      <div style={{ width:12, height:16, borderRadius:'40% 40% 50% 50% / 30% 30% 60% 60%', background:color, boxShadow:`0 0 16px ${color}cc, 0 0 36px ${color}66` }} />
    </div>
  );
}

interface Props {
  arc?: string;
  chapterIndex?: number;
}

export default function SceneIllustration({ arc = 'schatzsuche', chapterIndex = 0 }: Props) {
  const p = PALETTES[arc] ?? PALETTES.schatzsuche;
  const grainSvg = encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/><feColorMatrix values='0 0 0 0 0.95  0 0 0 0 0.85  0 0 0 0 0.65  0 0 0 0.18 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>`
  );

  // Probe the baked scene PNG. On success, fade it in on top of the procedural
  // scene. Re-probes whenever arc or chapterIndex changes.
  const bakedUrl = sceneBackgroundUrl(arc, chapterIndex);
  const [bakedOk, setBakedOk] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setBakedOk(false);
    const img = new Image();
    img.onload  = () => { if (!cancelled) setBakedOk(true); };
    img.onerror = () => { if (!cancelled) setBakedOk(false); };
    img.src = bakedUrl;
    return () => { cancelled = true; };
  }, [bakedUrl]);

  return (
    <div style={{ position:'relative', width:'100%', height:'100%', background:`linear-gradient(180deg, ${p.sky1} 0%, ${p.sky2} 62%, ${p.ground} 100%)`, overflow:'hidden', borderRadius:'inherit' }}>
      <Stars />

      {/* moon */}
      <div style={{ position:'absolute', top:'14%', right:'16%', width:74, height:74, borderRadius:'50%', background:p.moon, boxShadow:`0 0 42px ${p.moon}55, 0 0 96px ${p.moon}22` }} />

      {/* distant hill */}
      <div style={{ position:'absolute', bottom:'32%', left:0, right:0, height:110, background:p.tree3, clipPath:'polygon(0% 100%, 0% 60%, 10% 45%, 18% 55%, 28% 30%, 38% 50%, 48% 35%, 60% 55%, 70% 40%, 82% 55%, 92% 35%, 100% 50%, 100% 100%)' }} />

      {/* mid trees */}
      <div style={{ position:'absolute', bottom:'22%', left:0, right:0, display:'flex', alignItems:'flex-end', justifyContent:'space-around', padding:'0 2%' }}>
        {[0,1,2,3,4,5,6,7,8].map(i => <Tree key={i} height={80+(i%3)*28} color={p.tree2} />)}
      </div>

      {/* foreground trees */}
      <div style={{ position:'absolute', bottom:'14%', left:0, right:0, display:'flex', alignItems:'flex-end', justifyContent:'space-between', padding:'0 12px' }}>
        <Tree height={180} color={p.tree1} wide />
        <Tree height={140} color={p.tree1} wide />
        <div style={{ flex:1 }} />
        <Tree height={210} color={p.tree1} wide />
      </div>

      {/* ground */}
      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:'18%', background:`linear-gradient(180deg, ${p.ground}, ${p.ground}ee)` }}>
        <div style={{ position:'absolute', bottom:0, left:'30%', width:'40%', height:'100%', background:`radial-gradient(ellipse at 50% 120%, ${p.highlight}99 0%, transparent 60%)` }} />
      </div>

      <Lantern top="34%" left="22%" color={p.highlight} delay={0} />
      <Lantern top="44%" left="74%" color={p.highlight} delay={1.3} />
      <Lantern top="28%" left="50%" color={p.highlight} delay={2.1} />

      {/* baked scene PNG — fades in on top of the procedural scene when it loads */}
      <img
        src={bakedUrl}
        alt=""
        style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
          objectFit: 'cover',
          opacity: bakedOk ? 1 : 0,
          transition: 'opacity 380ms ease',
          pointerEvents: 'none',
        }}
        onLoad={() => setBakedOk(true)}
        onError={() => setBakedOk(false)}
      />

      {/* paper grain */}
      <div style={{ position:'absolute', inset:0, background:`url("data:image/svg+xml;utf8,${grainSvg}")`, mixBlendMode:'overlay', opacity:0.45, pointerEvents:'none' }} />

      {/* vignette */}
      <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse at 50% 50%, transparent 45%, rgba(0,0,0,0.5) 100%)', pointerEvents:'none' }} />
    </div>
  );
}
